/**
 * Monthly unit costs derived from unit_costs API rows.
 *
 * Rules (must match backend profit_service):
 * - monthly: amount_chf counts fully
 * - yearly: amount_chf / 12
 * - one_time: ignored for monthly profit
 */

/** Monthly-equivalent sum for unit_costs rows (excludes landlord deposit insurance premium). */
export function getUnitCostsTotal(unitCosts) {
  if (!Array.isArray(unitCosts)) return 0;
  return unitCosts.reduce((sum, row) => {
    const amt = Number(row?.amount_chf);
    if (!Number.isFinite(amt) || amt <= 0) return sum;
    const freq = String(row?.frequency || "monthly").trim().toLowerCase();
    if (freq === "monthly") return sum + amt;
    if (freq === "yearly") return sum + amt / 12;
    return sum;
  }, 0);
}

/** Sum amounts for rows whose cost_type matches (e.g. "Miete", "Nebenkosten"). */
export function sumUnitCostsByType(unitCosts, costTypeLabel) {
  if (!Array.isArray(unitCosts) || costTypeLabel == null || costTypeLabel === "") return 0;
  const label = String(costTypeLabel);
  return unitCosts.reduce((sum, row) => {
    if (String(row?.cost_type || "") !== label) return sum;
    const amt = Number(row?.amount_chf);
    if (!Number.isFinite(amt) || amt <= 0) return sum;
    const freq = String(row?.frequency || "monthly").trim().toLowerCase();
    if (freq === "monthly") return sum + amt;
    if (freq === "yearly") return sum + amt / 12;
    return sum;
  }, 0);
}

/** Monthly share of landlord insurance deposit premium (annual / 12). */
export function landlordDepositInsuranceMonthly(unit) {
  const t = String(unit?.landlordDepositType || "").trim().toLowerCase();
  if (t !== "insurance") return 0;
  const premium = Number(unit?.landlordDepositAnnualPremium);
  if (!Number.isFinite(premium) || premium <= 0) return 0;
  return premium / 12;
}

/** Full monthly running costs used in profitability (unit_costs monthly-equivalent + insurance monthly). */
export function getUnitMonthlyRunningCosts(unit, unitCosts) {
  return getUnitCostsTotal(unitCosts) + landlordDepositInsuranceMonthly(unit);
}

/**
 * The current-state recurring breakdown, projected from the BACKEND contract.
 *
 * WHY A PROJECTION AND NOT AN AGGREGATION. The figures are already computed:
 * `running_costs_breakdown` arrives on the unit detail read with every operating
 * bucket rounded and classified by `unit_running_costs_service`. Re-deriving them
 * from the raw `unit_costs` rows is what let the finance tab and the units list
 * drift apart in the first place -- the frontend had no allowlist, so a row typed
 * "Kaution" landed inside a headline the backend deliberately kept it out of.
 * This function therefore only renames and orders; it never adds.
 *
 * `unclassified_recurring_total_chf` is NOT projected. It is real money and it is
 * reported, but it is not an operating bucket and it is not inside the headline,
 * so showing it in this list would put a figure in a total it does not belong to.
 * It belongs to the data-quality note instead.
 *
 * A zero bucket is dropped, matching what this list has always done; a NEGATIVE
 * bucket is kept, because it IS part of the headline and a line the reader cannot
 * see cannot explain the number above it.
 *
 * @param {object|null|undefined} breakdown - `running_costs_breakdown` from the wire
 * @returns {{ typeKey: string, label: string, total: number }[]}
 */
export function projectRunningCostsBreakdown(breakdown) {
  if (!breakdown || typeof breakdown !== "object") return [];
  // Display order, chosen to match what this list showed before the migration.
  const BUCKETS = [
    ["rent_chf", "Miete"],
    ["utilities_chf", "Nebenkosten"],
    ["cleaning_chf", "Reinigung"],
    ["internet_chf", "Internet"],
    ["deposit_insurance_chf", "Kautionsversicherung"],
  ];
  return BUCKETS.map(([key, label]) => {
    const total = Number(breakdown[key]);
    return { typeKey: key, label, total: Number.isFinite(total) ? total : 0 };
  }).filter((b) => b.total !== 0);
}

/** Display order for known recurring cost_type values; then other keys; empty last. */
const RECURRING_COST_TYPE_ORDER = ["Miete", "Nebenkosten", "Reinigung", "Internet"];

function compareOneTimeCostBreakdown(a, b) {
  const rank = (typeKey) => {
    const k = String(typeKey || "").trim();
    if (k === "__empty__") return [2, ""];
    const idx = RECURRING_COST_TYPE_ORDER.indexOf(k);
    if (idx >= 0) return [0, String(idx).padStart(2, "0")];
    return [1, k.toLowerCase()];
  };
  const [ta, ka] = rank(a.typeKey);
  const [tb, kb] = rank(b.typeKey);
  if (ta !== tb) return ta - tb;
  return ka.localeCompare(kb, "de");
}

function sortOneTimeBreakdown(entries) {
  return [...entries].sort(compareOneTimeCostBreakdown);
}

/** Sum of one_time amount_chf across unit_costs rows. */
export function totalOneTimeUnitCosts(unitCosts) {
  if (!Array.isArray(unitCosts)) return 0;
  let sum = 0;
  for (const row of unitCosts) {
    if (String(row?.frequency || "").trim().toLowerCase() !== "one_time") continue;
    const amt = Number(row?.amount_chf);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    sum += amt;
  }
  return sum;
}

/**
 * One-time costs grouped by cost_type (full amount).
 * @returns {{ typeKey: string, label: string, total: number }[]}
 */
export function oneTimeUnitCostBreakdownEntries(unitCosts) {
  if (!Array.isArray(unitCosts)) return [];
  const map = new Map();
  for (const row of unitCosts) {
    if (String(row?.frequency || "").trim().toLowerCase() !== "one_time") continue;
    const amt = Number(row?.amount_chf);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    const ct = String(row?.cost_type || "").trim();
    const k = ct || "__empty__";
    map.set(k, (map.get(k) || 0) + amt);
  }
  const out = Array.from(map.entries())
    .map(([typeKey, total]) => ({
      typeKey,
      label: typeKey === "__empty__" ? "—" : typeKey,
      total,
    }))
    .filter((x) => x.total !== 0);
  return sortOneTimeBreakdown(out);
}

/**
 * Sum one_time amount_chf for rows whose created_at is in the given calendar month.
 * Uses ISO date prefix (YYYY-MM) from created_at; rows without created_at are skipped.
 * Display-only; not used in KPI or profit_service.
 */
export function sumOneTimeUnitCostsInCalendarMonth(unitCosts, year, month) {
  if (!Array.isArray(unitCosts)) return 0;
  const y = Number(year);
  const m = Number(month);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return 0;
  const prefix = `${y}-${String(m).padStart(2, "0")}`;
  let sum = 0;
  for (const row of unitCosts) {
    if (String(row?.frequency || "").trim().toLowerCase() !== "one_time") continue;
    const amt = Number(row?.amount_chf);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    const ca = row?.created_at;
    if (ca == null || typeof ca !== "string") continue;
    if (ca.slice(0, 7) !== prefix) continue;
    sum += amt;
  }
  return sum;
}
